import React, {} from 'react'
import ImageSwiper from './ImageSwiper';
import css from './ListingCardBox.module.css';
import { useConfiguration } from '../../context/configurationContext';
import { displayPrice } from '../../util/configHelpers';
import { isBookingProcessAlias, isNegotiationProcess } from '../../transactions/transaction';
import { formatMoney } from '../../util/currency';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { AvatarSmall, NamedLink, InlineTextButton } from '../../components';
import { createSlug, LISTING_PAGE_PARAM_TYPE_DRAFT } from '../../util/urlHelpers';
import { ensureListing } from '../../util/data';
import { resolveLatestProcessName } from '../../transactions/transaction';


function extractLocationFromAddress(address) {
  if (!address || typeof address !== 'string') return { neighborhood: '', city: '', state: '', country: '' };

  const parts = address.split(',').map(p => p.trim()).filter(Boolean);
  
  if (!parts.length) return { neighborhood: '', city: '', state: '', country: '' };

  // Filter out street addresses and building numbers (parts with street indicators or starting with numbers)
  const streetIndicators = [
    'street', 'str.', 'str', 'road', 'rd.', 'rd', 'ave', 'avenue', 'av', 'blvd', 
    'boulevard', 'lane', 'ln', 'dr', 'drive', 'st', 'way', 'highway', 'hwy', 
    'plaza', 'square', 'place', 'building'
  ];
  
  const filtered = parts.filter(part => {
    const lower = part.toLowerCase();
    
    // Skip if it's mostly a building number/address (starts with number and has street indicator)
    if (/^\d/.test(part)) {
      const hasStreetIndicator = streetIndicators.some(ind => lower.includes(ind));
      if (hasStreetIndicator) return false;
    }
    
    // Skip if contains street indicators
    const hasStreetIndicator = streetIndicators.some(ind => {
      const regex = new RegExp(`\\b${ind}\\b`, 'i');
      return regex.test(part);
    });
    if (hasStreetIndicator) return false;
    
    return true;
  });

  // Based on the example: "Intrepid Museum, New York City, NY, USA"
  // Format should be: Neighborhood, City, State, Country
  let neighborhood = '';
  let city = '';
  let state = '';
  let country = '';

  if (filtered.length >= 4) {
    // Full format: Neighborhood, City, State, Country
    neighborhood = filtered[0];
    city = filtered[1];
    state = filtered[2];
    country = filtered[3];
  } else if (filtered.length === 3) {
    // City, State, Country
    city = filtered[0];
    state = filtered[1];
    country = filtered[2];
  } else if (filtered.length === 2) {
    // City, Country or State, Country
    city = filtered[0];
    country = filtered[1];
  } else if (filtered.length === 1) {
    // Just Country or City
    country = filtered[0];
  }

  return { neighborhood, city, state, country };
}

function cleanText(str) {
  if (!str) return '';
  return String(str).trim();
}

// Generate random color for negotiation listings without images
const getRandomColor = (id) => {
  const colors = ['#2ECC71', '#3498DB', '#9B59B6', '#E74C3C', '#F39C12', '#1ABC9C'];
  const hash = id.split('').reduce((acc, char) => char.charCodeAt(0) + acc, 0);
  return colors[hash % colors.length];
};

const ListingCardBox = ({ listing, intl, reviews, toggleFavorites, profilePage }) => {
  const config = useConfiguration();
  const currentListing = ensureListing(listing);
  const fullAddress = currentListing?.attributes?.publicData?.location?.address || '';
  const { neighborhood, city, state, country } = extractLocationFromAddress(fullAddress);
  
  const { attributes = {}, author, images = [] } = currentListing || {};
  const { title = '', price, publicData = {} } = attributes;
  const id = currentListing?.id?.uuid || '';
  const slug = createSlug(title);

  // Check if this is a negotiation listing
  const { transactionProcessAlias } = publicData;
  const processName = transactionProcessAlias 
    ? resolveLatestProcessName(transactionProcessAlias.split('/')[0])
    : null;
  const isNegotiation = isNegotiationProcess(processName);
  const hasImages = images && images.length > 0;

  const listingReview = reviews?.filter(review => {
    const reviewListingId = review?.listing?.id?.uuid;
    return reviewListingId === currentListing?.id?.uuid;
  }) || [];

  const averageRating = listingReview.length > 0
    ? listingReview.reduce((sum, r) => {
        const rating = r?.attributes?.rating || 0;
        return sum + rating;
      }, 0) / listingReview.length
    : null;

  // Price formatter
  const priceData = (price, currency, intl) => {
    if (price && price.currency === currency) {
      const formattedPrice = formatMoney(intl, price);
      return { formattedPrice, priceTitle: formattedPrice };
    } else if (price) {
      return {
        formattedPrice: intl.formatMessage(
          { id: 'ListingCard.unsupportedPrice' },
          { currency: price.currency }
        ),
        priceTitle: intl.formatMessage(
          { id: 'ListingCard.unsupportedPriceTitle' },
          { currency: price.currency }
        ),
      };
    }
    return {};
  };

  // Renders price if allowed
  const PriceMaybe = ({ price, publicData, config, intl }) => {
    const { listingType, unitType } = publicData || {};
    const validListingTypes = config.listing.listingTypes;
    const foundConfig = validListingTypes.find(conf => conf.listingType === listingType);
    const showPrice = displayPrice(foundConfig);

    if (!showPrice || !price) return null;

    const { formattedPrice, priceTitle } = priceData(price, config.currency, intl);
    
    // Use unitType from publicData, default to 'night' if not provided
    const displayUnit = unitType || 'night';
    
    return (
      <div className={css.price}>
        <div className={css.priceValue} title={priceTitle}>
          {formattedPrice}/{displayUnit}
        </div>
      </div>
    );
  };

  // Build display location
  const getDisplayLocation = () => {
    const parts = [];
    
    if (neighborhood) parts.push(neighborhood);
    if (city) parts.push(city);
    if (state) parts.push(state);
    if (country) parts.push(country);
    
    return parts.filter(Boolean).join(', ');
  };

  const displayLocation = getDisplayLocation();

  // Get color for negotiation listing
  const backgroundColor = getRandomColor(id);

  return (
    <div className={css.cardBox} data="cardBox">
      <div className={css.cardImgBox} data="cardImgBox">
        {hasImages ? (
          <div className={css.imgBox}>
            <ImageSwiper images={images} id={id} slug={slug} />
          </div>
        ) : isNegotiation ? (
          <NamedLink
            className={css.negotiationPlaceholder}
            name="ListingPage"
            params={{ id, slug }}
            style={{ backgroundColor }}
          >
            <span className={css.negotiationTitle}>{title}</span>
          </NamedLink>
        ) : (
          <div className={css.noImagePlaceholder}>
            <span>No image</span>
          </div>
        )}
      </div>
      <div className={css.cardDetails} data="cardDetails">
        <div className={css.titleRow} data="titleRow">
          <div className={css.title} data="title">{title}</div>
          {averageRating !== null && !isNaN(averageRating) ? (
            <div className={css.ratingText}>
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="14" viewBox="0 0 15 14" fill="none">
                <path d="M7.5 0L9.18386 5.18237H14.6329L10.2245 8.38525L11.9084 13.5676L7.5 10.3647L3.09161 13.5676L4.77547 8.38525L0.367076 5.18237H5.81614L7.5 0Z" fill="#8C8C8C"/>
              </svg>
              <span>{averageRating.toFixed(1)}</span>
            </div>
          ) : null}
        </div>
        <div className={css.userRow} data="userRow">
          <AvatarSmall user={author} className={css.userImage} />
          <NamedLink
            className={css.userProfileLink}
            name="ListingPage"
            params={{ id, slug }}
          >
            Hosted By - {author?.attributes?.profile?.displayName}
          </NamedLink>
        </div>

        <div className={css.cardPrice} data="cardPrice">
          <PriceMaybe price={price} publicData={publicData} config={config} intl={intl} />
          {displayLocation && (
            <div className={css.cardAddress} data="cardAddress">
              {displayLocation}
            </div>
          )}
          
        </div>
        {(publicData?.guest || publicData?.rooms) && (
          <div className={css.cardFeatureRow} data="cardFeatureRow">
            <div className={css.featureBox}>
              {publicData?.guest && (
                <>Guests: <span className={css.num}>{publicData.guest}</span></>
              )}
              {publicData?.guest && publicData?.rooms && ' | '}
              {publicData?.rooms && (
                <>Rooms: <span className={css.num}>{publicData.rooms}</span></>
              )}
            </div>
          </div>
        )}

        {!profilePage && 
          <NamedLink
            className={css.editListing}
            title="editListing"
            name="EditListingPage"
            params={{ id, slug, type: LISTING_PAGE_PARAM_TYPE_DRAFT, tab: 'details' }}
          >
              Edit Listing
          </NamedLink>
        }
      </div>
    </div>
  );
};

export default ListingCardBox;